import Comment from '@/models/Comment';
import PlantDailyRecord from '@/models/PlantDailyRecord';
import PlantDailyRecordService from '@/services/PlantDailyRecordService';
import { defineStore } from 'pinia';
import { useRepo } from 'pinia-orm';

export const usePlantDailyRecordsStore = defineStore('plantDailyRecords', {
  state: () => ({}),

  getters: {
    models: () => useRepo(PlantDailyRecord),
  },

  actions: {
    async createRecord(plantId: Id, recordForm: ApiRequests['api.v1.plant.daily.records.store']) {
      const [data, error] = await PlantDailyRecordService.create(plantId, recordForm);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateRecord(
      id: Id,
      recordForm: ApiRequests['api.v1.plant.daily.records.update'],
      transformData: { day: string },
    ) {
      const [data, error] = await PlantDailyRecordService.update(id, recordForm, transformData);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async approve(id: Id) {
      const [data, error] = await PlantDailyRecordService.approve(id);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async unapprove(id: Id) {
      const [data, error] = await PlantDailyRecordService.unapprove(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async paid(id: Id, paid: ApiRequests['api.v1.plant.daily.records.paid']) {
      const [data, error] = await PlantDailyRecordService.paid(id, paid);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async fetchRecords(
      plantId: Id,
      form: ApiRequests['api.v1.plant.daily.records.index'] = {},
    ): Promise<CursorPaginator<PlantDailyRecord> & { has_more: boolean }> {
      const [data, error] = await PlantDailyRecordService.index(plantId, form);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data as unknown as CursorPaginator<PlantDailyRecord> & { has_more: boolean };
    },

    async fetchRecord(recordId: Id) {
      const [data, error] = await PlantDailyRecordService.show(recordId);

      if(error) {
        throw error;
      }

      // Save comments separately as pinia orm cannot handle saving the morph relationship
      const comments = data.comments;

      delete data.comments;
      const commentsRepo = useRepo(Comment);

      commentsRepo.save(comments);

      this.models.save(data);

      return data;
    },

    async splitRecord(
      id: Id,
      request: ApiRequests['api.v1.plant.daily.records.split'],
      transformData: { newDay: string; oldDay: string },
    ) {
      const [data, error] = await PlantDailyRecordService.split(id, request, transformData);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async comment(id: Id, request: ApiRequests['api.v1.plant.daily.records.comments']) {
      const [data, error] = await PlantDailyRecordService.comment(id, request);

      if(error) {
        throw error;
      }

      const comments = useRepo(Comment);

      comments.save(data);

      return data;
    },

    async vendor(id: Id, request: ApiRequests['api.v1.plant.daily.records.vendor']) {
      const [data, error] = await PlantDailyRecordService.vendor(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async search(projectId: Id, form: ApiRequests['api.v1.plant.daily.records.search']) {
      const [data, error] = await PlantDailyRecordService.search(projectId, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async deleteRecord(id: Id) {
      const [data, error] = await PlantDailyRecordService.delete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(data.id);

      return data;
    },

    async fetchSignedRecords(
      claimId: Id,
      signedParams: unknown,
      plantId: Id,
      page = 1,
    ) {
      const [data, error] = await PlantDailyRecordService.signed.records(
        claimId,
        signedParams,
        {
          plant_id: Number(plantId),
          page,
        },
      );

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },
  },
});
